class CookieBanner extends HTMLElement {
  constructor() {
    super();
    this.launcherButton = this.querySelector('[data-cookie-launcher]');
    this.closeButton = this.querySelector('[data-cookie-close]');
    this.acceptButton = this.querySelector('[data-cookie-accept]');
    this.rejectButton = this.querySelector('[data-cookie-reject]');
    this.saveButton = this.querySelector('[data-cookie-save]');
    this.togglePreferencesButton = this.querySelector('[data-cookie-toggle-preferences]');
    this.preferencesPanel = this.querySelector('[data-cookie-preferences]');
    this.preferenceInputs = Array.from(this.querySelectorAll('[data-consent-category]'));
    this.panel = this.querySelector('.cookie-card');
    this.status = this.querySelector('[data-cookie-status]');
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.launcherButton?.addEventListener('click', () => this.toggleOpen());
    this.closeButton?.addEventListener('click', () => this.close());

    this.acceptButton?.addEventListener('click', () => {
      this.applyConsent({
        analytics: true,
        marketing: true,
        preferences: true,
      });
    });

    this.rejectButton?.addEventListener('click', () => {
      this.applyConsent({
        analytics: false,
        marketing: false,
        preferences: false,
      });
    });

    this.saveButton?.addEventListener('click', () => {
      this.applyConsent({
        analytics: this.isChecked('analytics'),
        marketing: this.isChecked('marketing'),
        preferences: this.isChecked('preferences'),
      });
    });

    this.togglePreferencesButton?.addEventListener('click', () => {
      const shouldOpen = this.preferencesPanel.hasAttribute('hidden');
      this.preferencesPanel.toggleAttribute('hidden', !shouldOpen);
      this.saveButton.toggleAttribute('hidden', !shouldOpen);
      this.togglePreferencesButton.setAttribute('aria-expanded', String(shouldOpen));
      if (shouldOpen) this.open();
    });

    document.addEventListener('click', (event) => {
      if (!this.classList.contains('is-open')) return;
      if (this.contains(event.target)) return;
      this.close();
    });

    document.addEventListener('keydown', (event) => {
      // Echap ferme visuellement le panneau, mais ne vaut JAMAIS consentement
      // ni refus : aucun appel a setTrackingConsent ici. Tant que rien n'a
      // ete choisi, shouldShowBanner() reste vrai et le bandeau se rouvrira.
      if (event.key === 'Escape') this.close();
    });

    document.addEventListener('visitorConsentCollected', () => {
      if (!this.customerPrivacy) return;
      this.syncInputsFromConsent();
      const pending = this.customerPrivacy.shouldShowBanner();
      this.classList.toggle('cookie-banner--pending', pending);
      if (!pending) this.close();
    });

    const ready = (error) => {
      if (error || !window.Shopify?.customerPrivacy) return;
      this.customerPrivacy = window.Shopify.customerPrivacy;
      this.syncInputsFromConsent();
      this.hidden = false;
      const pending = this.customerPrivacy.shouldShowBanner();
      this.classList.toggle('cookie-banner--pending', pending);
      // Ne pas superposer deux demandes si le bandeau Shopify est deja visible.
      const nativeBanner = document.getElementById('shopify-pc__banner');
      if (pending && !nativeBanner?.getClientRects().length) this.open();
    };
    if (window.Shopify?.customerPrivacy) ready();
    else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], ready);
  }

  open() {
    if (this.panel) this.panel.hidden = false;
    this.classList.add('is-open');
    this.launcherButton?.setAttribute('aria-expanded', 'true');
    // Panneau non modal : la page et le bouton flottant restent accessibles.
    // Focus pose sur le premier element utile du panneau : le bouton fermer
    // s'il existe, sinon le panneau lui-meme. Necessaire au clavier et aux
    // lecteurs d'ecran (le panneau est un role="dialog").
    const target = this.closeButton || this.panel;
    if (target) {
      target.focus({ preventScroll: true });
    }
  }

  close() {
    const wasOpen = this.classList.contains('is-open');
    const restoreFocus = wasOpen && this.panel?.contains(document.activeElement);
    this.classList.remove('is-open');
    if (this.panel) this.panel.hidden = true;
    this.launcherButton?.setAttribute('aria-expanded', 'false');
    this.panel?.setAttribute('aria-modal', 'false');
    // Le focus ne revient sur l'icone que s'il etait dans le panneau :
    // sinon on volerait le focus a un visiteur en train de lire la page.
    if (restoreFocus) {
      this.launcherButton?.focus({ preventScroll: true });
    }
  }

  toggleOpen() {
    if (this.classList.contains('is-open')) {
      this.close();
      return;
    }

    this.open();
  }

  isChecked(category) {
    return this.querySelector(`[data-consent-category="${category}"]`)?.checked || false;
  }

  syncInputsFromConsent() {
    if (!this.customerPrivacy || typeof this.customerPrivacy.currentVisitorConsent !== 'function') return;

    const consent = this.customerPrivacy.currentVisitorConsent();
    this.preferenceInputs.forEach((input) => {
      const value = consent[input.dataset.consentCategory];
      input.checked = value === true || value === 'yes' || value === 'granted';
    });
  }

  applyConsent(consent) {
    if (this.saving || !this.customerPrivacy?.setTrackingConsent) return;
    this.saving = true;
    this.toggleButtons(true);
    this.setStatus(this.dataset.saving);
    this.panel?.setAttribute('aria-busy', 'true');
    const request = this.consentRequest = (this.consentRequest || 0) + 1;
    const finish = (error) => {
      if (request !== this.consentRequest) return;
      window.clearTimeout(timeout);
      this.saving = false;
      this.toggleButtons(false);
      this.panel?.removeAttribute('aria-busy');
      this.setStatus(error ? this.dataset.error : '');
      if (error) return;
      this.syncInputsFromConsent();
      window.dispatchEvent(new CustomEvent('vd:cookie-consent-updated', { detail: consent }));
      this.classList.remove('cookie-banner--pending');
      this.close();
    };
    // Une API lente ne doit jamais bloquer la fermeture ni les choix suivants.
    const timeout = window.setTimeout(() => finish(true), 10000);
    try {
      this.customerPrivacy.setTrackingConsent(consent, (error) => finish(Boolean(error)));
    } catch (_) {
      finish(true);
    }
  }

  setStatus(message) {
    if (!this.status) return;
    this.status.textContent = message || '';
    this.status.hidden = !message;
  }

  toggleButtons(disabled) {
    [this.acceptButton, this.rejectButton, this.saveButton, this.togglePreferencesButton].forEach((button) => {
      if (button) button.disabled = disabled;
    });
  }
}

customElements.define('cookie-banner', CookieBanner);
